import { useMemo, useRef, useState } from 'react';
import {
  withStyles,
  createTableProps,
  SingleContact,
  Tooltip,
  MaterialSymbol,
  Box,
  GetColorStatus,
  capitalizeFirstLetterOfWords,
} from 'vopay';
import { Link } from 'react-router-dom';
import styles from './useTableConfig.module.scss';
import { Chip, TransactionStatus } from 'UI';
import PaymentMethodDropdownMenu from '@/components/Customers/CustomersPage/PaymentMethodsDropdown/PaymentMethodsDropdown';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

export default function useTableConfig(contacts: SingleContact[]) {
  const [emailOrPhone, setEmailOrPhone] = useState<'email' | 'phone'>('email');
  const { t } = useTranslation('translations', { keyPrefix: 'Contacts' });

  return createTableProps({
    id: 'contact-accounts',
    zebra: true,
    rows: contacts.map((c) => ({
      id: c.ContactID,
      data: c,
    })),
    columns: [
      {
        id: 'ContactName',
        header: t('name', {
          defaultValue: 'Name',
        }),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
        },
        template: ({ data }) => (
          <Link to={`/contacts/${data.ContactID}`} className={cn('link')}>
            {data.ContactName}
          </Link>
        ),
      },
      {
        id: 'Email',
        header: (
          <Box gap="sm" ai="center">
            {t('email', {
              defaultValue: 'Email',
            })}
            <MaterialSymbol icon={'switch_left'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
        },
        visible: emailOrPhone === 'email',
        template: ({ data }) => data.EmailAddress,
        onHeaderClick: () => {
          setEmailOrPhone('phone');
        },
      },
      {
        id: 'PhoneNumber',
        header: (
          <Box gap="sm" ai="center">
            {t('phone', {
              defaultValue: 'Phone Number',
            })}
            <MaterialSymbol icon={'switch_right'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
        },
        visible: emailOrPhone === 'phone',
        template: ({ data }) => formatPhoneNumber(data.PhoneNumber),
        onHeaderClick: () => {
          setEmailOrPhone('email');
        },
      },
      {
        id: 'type',
        align: 'left',
        template: ({ data }) => (data.IsBusiness ? 'Business' : 'Individual'),
        header: t('contactType', {
          defaultValue: 'Contact Type',
        }),
      },
      {
        id: 'Labels',
        align: 'left',
        header: t('labels', {
          defaultValue: 'Labels',
        }),
        template: ({ data }) => <Labels labels={data.Labels} />,
      },
      {
        id: 'status',
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus
              status={GetColorStatus(data.IsActive ? 'active' : 'inactive')}
              label={capitalizeFirstLetterOfWords(
                data.IsActive ? t('active', { defaultValue: 'Active' }) : t('inactive', { defaultValue: 'Inactive' }),
              )}
            />
          </Box>
        ),
        header: t('status', {
          defaultValue: 'Status',
        }),
      },
      {
        id: 'controls',
        align: 'right',
        template: ({ data }) => {
          return (
            <div className={cn('controls')}>
              <PaymentMethodDropdownMenu
                disabled={!data.IsActive}
                triggerSize="small"
                accountName={data.ContactName}
                accountID={data.ContactID}
                email={data.EmailAddress}
              />
            </div>
          );
        },
      },
    ],
  });
}

function Labels({ labels }: { labels: string[] }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation('translations', { keyPrefix: 'Contacts' });

  const overflowing = useMemo(() => {
    if (!ref.current) return false;

    return ref.current.scrollHeight > ref.current.clientHeight;
  }, [ref.current]);

  if (!labels.length) return <>-</>;

  return (
    <div className={cn('labelsContainer')}>
      <div ref={ref} className={cn('labels')}>
        {labels.map((label, idx) => (
          <Chip key={label + idx} label={label} className={cn('chip')} />
        ))}
      </div>

      {overflowing && (
        <Tooltip
          hasButton={false}
          customElement={
            <div className={cn('viewAll')}>
              {t('viewAll', {
                defaultValue: 'View All',
              })}
            </div>
          }
          information={
            <div className={cn('labelsTooltip')}>
              <h3>
                {t('allLabels', {
                  defaultValue: 'All Labels',
                })}
              </h3>

              <ul>
                {[...labels].map((label, idx) => (
                  <li key={label + idx}>
                    <Chip label={label} className={cn('label')} />
                  </li>
                ))}
              </ul>
            </div>
          }
        />
      )}
    </div>
  );
}

function formatPhoneNumber(phoneNumber: string): string {
  // Remove all non-numeric characters
  const cleaned = `${phoneNumber}`.replace(/\D/g, '');

  // Check the length of the cleaned number
  if (cleaned.length < 10 || cleaned.length > 12) {
    // If the length is invalid, return the original input
    return phoneNumber;
  }

  let countryCode = '1'; // Default country code
  let localNumber = cleaned;

  if (cleaned.length === 11) {
    // If the length is 11, assume the first digit is the country code
    countryCode = cleaned[0];
    localNumber = cleaned.slice(1);
  } else if (cleaned.length === 12) {
    // If the length is 12, assume the first two digits are the country code
    countryCode = cleaned.slice(0, 2);
    localNumber = cleaned.slice(2);
  }

  // Format the local number
  const match = /^(\d{3})(\d{3})(\d{4})$/.exec(localNumber);
  if (match) {
    return `+${countryCode} (${match[1]}) ${match[2]}-${match[3]}`;
  }

  return phoneNumber;
}
